import * as Y from 'yjs';

import { LazyValue } from './lazy-value';
import { DisposableElement } from './types';
import { registerIntegrateHandler, unregisterIntegrateHandler } from './utils';

/**
 * Represents an element inside a Y.Map, with a mapper function applied. If the element does not exist
 * inside the map yet, or the element is invalid (represented by the mapper function returning undefined),
 * the "value" property will be undefined. The element will only be initialized and attached to the map
 * when the "valueRequired" property is accessed.
 * The "value" property should be used in places where the map element is read, while the "valueRequired"
 * property should be used in places where the map element is modified.
 */
export class SynchronizedMapElement<T>
  extends LazyValue<T>
  implements DisposableElement, LazyValue<T>
{
  /**
   * @param mapper Maps/wraps the map element to what will be available as "value". If the map element is
   *   invalid, should return undefined. Must not return undefined for the value returned by initialize().
   * @param initialize Should return a raw (unmapped) value that will be attached to the map when the
   *   "valueRequired" property is accessed and the map element does not exist yet or is invalid.
   */
  constructor(
    private input: Y.Map<unknown>,
    private key: string,
    mapper: (input: unknown) => T | undefined,
    initialize: () => any
  ) {
    super(
      () => mapper(input.get(key)),
      () => {
        const val = initialize();
        input.set(key, val);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return mapper(val)!;
      }
    );

    input.observe(this.onValueChanged);
    registerIntegrateHandler(input, this.onIntegrate);
  }

  private onValueChanged = (e: Y.YMapEvent<any>) => {
    if (e.keysChanged.has(this.key)) {
      this.initializeValue(false);
    }
  };

  private onIntegrate = () => {
    this.initializeValue(false);
  };

  dispose(): void {
    super.dispose();

    this.input.unobserve(this.onValueChanged);
    unregisterIntegrateHandler(this.input, this.onIntegrate);
  }
}
